import {takeWhile, catchError} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {DetailControlOption, ValuesObject} from '../detail-tabconfig';
import {FilterResult} from '../../filterresult';
import {ResourceService} from '../../resource.service';
import {AbstractControlComponent} from './abstract-control';
import {Observable} from 'rxjs';
import {DetailService} from '../detail.service';
import {ViewTabComponent} from '../viewtab.component';
import {optioncontrolRequired} from './validators/optioncontrol-required-validator';
import {InvalidItemSourceObjectError} from '../../../error/errordefinitions';
import {ErrorService} from '../../../error/error.service';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {CtrlDropdownComponent} from './ctrl-dropdown.component';
import {CacheService} from '../../../cache/cache.service';

@Component({
  selector: 'ctrl-radiobuttons',
  template: `
    <mat-radio-group [formControl]="formControl">
      @for (opt of options; track opt) {
        <span>
          @if (getCaughtObservable(opt.Value) | async) {
            <mat-radio-button
              matTooltipShowDelay="300"
              [matTooltip]="(getCaughtObservable(opt.Hint) | async)?.Values"
              [value]="(getCaughtObservable(opt.Value) | async)?.Values"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              {{ (getCaughtObservable(opt.Caption) | async)?.Values }}
            </mat-radio-button>
          }
        </span>
      }
    </mat-radio-group>
    @if (
      (getCaughtObservable(isEnabled) | async) &&
      formControl.touched &&
      formControl.hasError('required')
    ) {
      <mat-error>
        <language key="ControlErrorRequired" defaultValue="This field is required."></language>
      </mat-error>
    }
  `,
  styles: [
    `
      /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      mat-radio-button {
        margin-right: 20px;
      } /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-radio-label {
        align-items: center;
      } /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-radio-container {
        top: -1px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlRadiobuttonsComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    private cacheService: CacheService,
    private resourceService: ResourceService,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(_parent, 'SelectedValue', 'SelectedValue', changeDetector, _ngZone, errorService);
  }

  @Input() objectType: string;

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([optioncontrolRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
    CtrlDropdownComponent.regenerateOptions(
      this._config,
      this.resourceService,
      this.cacheService,
      this.objectType,
      this.envConfig,
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(options => {
        this.options = options;
        this.changeDetector.markForCheck();
      });
  }

  options: DetailControlOption[] = [];

  protected formatSource(source: any): any {
    if (source === '' || source == null) return DetailControlOption.NullValueOverwrite;

    if (Array.isArray(source)) {
      if (source.length === 0) {
        return DetailControlOption.NullValueOverwrite;
      } else {
        return source[0];
      }
    } else {
      return source;
    }
  }

  protected formatTarget(value: any): any {
    if (value === DetailControlOption.NullValueOverwrite) value = null;
    return value;
  }
}
